import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import ItemClass from 'src/models/item/ItemClass'
import Location from 'src/models/location/Location'
import Model, { ID, IsoDate, JsonObject, MODEL_LEVEL } from 'src/models/model'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import RegisterStation from 'src/models/register/RegisterStation'
import itemDeliveryTypeOptionsComputer from 'src/services/item-delivery/ItemDeliveryTypeOptionsComputer'

export const SALES_ITEM_RESTRICTION_TYPES = {
  NONE: null,
  INFO: 'info',
  WARN: 'warn',
  ADMIN_ONLY: 'admin-only',
  RESTRICT: 'restrict'
}

SALES_ITEM_RESTRICTION_TYPES.ALL = _.values(SALES_ITEM_RESTRICTION_TYPES)

SALES_ITEM_RESTRICTION_TYPES.INTERVENTION = [
  SALES_ITEM_RESTRICTION_TYPES.WARN,
  SALES_ITEM_RESTRICTION_TYPES.ADMIN_ONLY,
  SALES_ITEM_RESTRICTION_TYPES.RESTRICT
]

export const AVAILABILITY_BADGE_DISPLAY_TYPES = {
  ALWAYS: 'always',
  LOW_STOCK: 'low-stock',
  OUT_OF_STOCK: 'out-of-stock',
  NEVER: null
}

AVAILABILITY_BADGE_DISPLAY_TYPES.ALL = _.values(AVAILABILITY_BADGE_DISPLAY_TYPES)

AVAILABILITY_BADGE_DISPLAY_TYPES.LOW_STOCK_THRESHOLD_REQUIRED = [
  AVAILABILITY_BADGE_DISPLAY_TYPES.ALWAYS,
  AVAILABILITY_BADGE_DISPLAY_TYPES.LOW_STOCK
]

export const AVAILABILITY_BADGE_COLORS = {
  POSITIVE: 'positive',
  WARNING: 'warning',
  NEGATIVE: 'negative'
}

export default class InventoryStrategy extends Model {
  static name = 'InventoryStrategy'

  static isConfiguration = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Inventory]

  static define () {
    this.fields = {
      id: {
        type: ID
      },
      startDate: {
        type: IsoDate,
        default: null
      },
      endDate: {
        type: IsoDate,
        default: null,
        output: true
      },
      isActive: {
        type: Boolean,
        default: false,
        output: true
      },
      isActivationInProgress: {
        type: Boolean,
        default: false,
        output: true
      },
      defaultInventoryLocationId: {
        type: ID,
        default: null
      },
      locations: {
        type: [Location],
        // Not a reference!! The client config only contains locations from the
        // current site, so inventory strategies from different sites can't
        // resolve them. This breaks transfers between sites.
        encode (data) {
          data.locationIds = _.map(this.locations.data, 'id')
        }
      },
      registerStations: {
        type: [RegisterStation],
        // Not a reference!! The client config only contains register stations
        // from the current site, so inventory strategies from different sites
        // can't resolve them. This breaks transfers between sites.
        encode (data) {
          data.registerStationIds = _.map(this.registerStations.data, 'id')
        }
      },
      itemClasses: {
        // Not a reference!! (For consistency)
        type: [ItemClass],
        encode (data) {
          data.itemClassIds = _.map(this.itemClasses.data, 'id')
        }
      },
      customFields: {
        type: JsonObject,
        default: () => ({})
      },
      config: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          propagateInventoryItemCostPriceChangesToSalesItems: false,
          inventoryItemAvailabilitiesAndConstraintsSalesItemRestrictionType: SALES_ITEM_RESTRICTION_TYPES.NONE,
          inventoryItemAvailabilitiesAndConstraintsAvailabilityBadgeDisplayType: AVAILABILITY_BADGE_DISPLAY_TYPES.ALWAYS,
          inventoryItemAvailabilitiesAndConstraintsAvailabilityBadgeLowStockThreshold: 10
        })
      },
      action: {
        type: String,
        input: true,
        default: null
      }
    }

    this.computed = {
      defaultInventoryLocation () {
        return _.find(this.locations.data, location => location.id === this.defaultInventoryLocationId)
      },
      isImmutable () {
        // When active or already ended, it can no longer be edited.
        return this.isActive || !_.isNil(this.endDate)
      },
      inventoryRestrictionType () {
        return this.config.inventoryItemAvailabilitiesAndConstraintsSalesItemRestrictionType
      },
      availabilityBadgeDisplayType () {
        return this.config.inventoryItemAvailabilitiesAndConstraintsAvailabilityBadgeDisplayType
      },
      availabilityBadgeLowStockThreshold () {
        return this.config.inventoryItemAvailabilitiesAndConstraintsAvailabilityBadgeLowStockThreshold
      },
      excludeOutOfStockItemsForKioskAndConsumer () {
        return !!this.inventoryRestrictionType
      }
    }

    this.watchers = [
      [
        function () { return this.locations?.data },
        function () {
          if (_.isNil(this.locations)) return

          const locationIds = _.map(this.locations.data, 'id')

          if (!_.includes(locationIds, this.defaultInventoryLocationId)) {
            this.defaultInventoryLocationId = _.first(locationIds) || null
          }
        }
      ]
    ]
  }

  allowInventoryAtRegisterStation (registerStationId, inventoryItem) {
    const registerStationIds = _.map(this.registerStations?.data, 'id')
    const itemClassIds = _.map(this.itemClasses?.data, 'id')
    return !!registerStationId && _.includes(registerStationIds, registerStationId) && (
      !inventoryItem || _.includes(itemClassIds, inventoryItem.itemClass?.id)
    )
  }

  allowInventoryAtLocation (locationId) {
    const locationIds = _.map(this.locations?.data, 'id')
    return !!locationId && _.includes(locationIds, locationId)
  }

  registerSourceLocations (item, registerStation) {
    const registerStationLocation = registerStation?.registerType.location
    const registerStationLocationGroup = registerStationLocation?.locationGroup

    if (registerStationLocationGroup) {
      return _.filter(item.locations.data, location => location.locationGroup?.id === registerStationLocationGroup.id)
    } else {
      return []
    }
  }

  registerSourceLocation (item, deliveryType, registerStation) {
    const registerStationLocation = registerStation?.registerType.location

    if (
      this.colocationRulesApplyForDeliveryType(deliveryType) &&
      registerStationLocation?.isValidPrintLocation &&
      item.hasLocation(registerStationLocation)
    ) {
      return registerStationLocation
    }

    return _(this.registerSourceLocations(item, registerStation)).filter('isValidPrintLocation').first()
  }

  screenSourceLocations (item, screen) {
    const screenLocationGroup = screen?.locationGroup

    if (screenLocationGroup) {
      return _.filter(item.locations.data, location => location.locationGroup?.id === screenLocationGroup.id)
    } else {
      return []
    }
  }

  screenSourceLocation (item, screen) {
    return _(this.screenSourceLocations(item, screen)).filter('isValidPrintLocation').first()
  }

  defaultSourceLocation (item) {
    const sourceLocations = item.locations.data
    return _(sourceLocations).filter('isValidPrintLocation').first() || _.first(sourceLocations)
  }

  colocationRulesApplyForDeliveryType (deliveryType) {
    return _.includes(DELIVERY_TYPE_OPTIONS.SYNCHRONOUS, deliveryType)
  }

  itemColocatedWithRegister (item, registerStation) {
    const registerStationLocation = registerStation?.registerType.location

    return !!registerStationLocation && item.hasLocation(registerStationLocation)
  }

  getDefaultDeliveryTypeForItem (item, order) {
    const deliveryTypeOptions = itemDeliveryTypeOptionsComputer.deliveryTypeOptions(item, order)
    return _.find(deliveryTypeOptions, deliveryTypeOption => deliveryTypeOption?.value !== ORDER_ITEM_TYPES.VIRTUAL)?.value
  }

  sourceLocation ({ deliveryType, item, screen }) {
    const currentRegisterStation = this.context.currentRegisterClient?.registerStation

    let location

    if (this.colocationRulesApplyForDeliveryType(deliveryType) && this.itemColocatedWithRegister(item, currentRegisterStation)) {
      location = (
        this.registerSourceLocation(item, deliveryType, currentRegisterStation) ||
        this.screenSourceLocation(item, screen) ||
        this.defaultSourceLocation(item)
      )
    } else {
      location = (
        this.screenSourceLocation(item, screen) ||
        this.registerSourceLocation(item, deliveryType, currentRegisterStation) ||
        this.defaultSourceLocation(item)
      )
    }

    return this.allowInventoryAtLocation(location?.id) ? location : this.defaultInventoryLocation
  }

  allowSourceFromRegisterStation ({ deliveryType, item, registerStation, screen }) {
    if (!registerStation) return false

    const sourceLocation = this.sourceLocation({ deliveryType, item, screen })
    const registerStationLocation = registerStation.registerType.location

    /**
     * Availability for each register station includes the availability from
     * both the register station itself, and either the register station's
     * location (if part of the inventory strategy), or the default inventory
     * location. This is because a recipe can be made up of multiple inventory
     * items, not all of which are inventoriable by register station. Therefore,
     * we always include the availability at a location as well.
     */
    const resolvedRegisterStationLocation = this.allowInventoryAtLocation(registerStationLocation?.id)
      ? registerStationLocation
      : this.defaultInventoryLocation

    return (
      this.allowInventoryAtRegisterStation(registerStation?.id) &&
      sourceLocation.id === resolvedRegisterStationLocation.id
    )
  }

  sourceLocationKey ({ deliveryType, item, screen }) {
    const currentRegisterStation = this.context.currentRegisterClient?.registerStation

    if (this.allowSourceFromRegisterStation({ deliveryType, item, registerStation: currentRegisterStation, screen })) {
      return 'register_stations'
    } else {
      return 'locations'
    }
  }

  sourceLocationId ({ deliveryType, item, screen }) {
    const currentRegisterStation = this.context.currentRegisterClient?.registerStation

    if (this.allowSourceFromRegisterStation({ deliveryType, item, registerStation: currentRegisterStation, screen })) {
      return currentRegisterStation.id
    } else {
      const sourceLocation = this.sourceLocation({ deliveryType, item, screen })
      return sourceLocation.id
    }
  }
}
